import env from '#shared/config/env.js';

import validateField from '#frontend/util/validateField.js';

import isObject from './isObject.js';
import makeRequest from './makeRequest.js';

/**
 * Check if given zip code belongs to some state and city
 * @param {string} zipCode ZIP code to check
 * @param {string} countryCode country code to check
 * @returns {Promise}
 */
export async function isStateZipCode(zipCode, countryCode) { // eslint-disable-line func-style
	try {
		const response = await makeRequest.post('/api/v1/cart/validate/zip-code', {
			credentials: 'same-origin',
			json: {
				value: zipCode,
				country: countryCode
			}
		});
		return response.body.data;
	} catch (error) {
		if (error.app && error.app.status === 401) {
			return this.$store.commit('SET_ERROR', error);
		} else {
			throw error;
		}
	}
}

/**
 * Validate ZIP code according to country and return errors
 * @param {object} zipCodeInput Input with value to check
 * @param {object} customer Customer info
 * @param {string} [ariaDescribedBy] Id of element with error description
 * @param {Array} previousInputGroupErrors Array of currently shown input errors
 * @param {string} errorMessage Message to be shown in ZIP code error
 * @returns {Array}
 */
export function validateZipFormat(zipCodeInput, customer, ariaDescribedBy, previousInputGroupErrors, errorMessage) { // eslint-disable-line func-style
	const zipCodeType = env.customZipValidationCountries.includes(customer.billing.country) ? `zipCode${customer.billing.country}` : 'zipCode';
	const inputGroupErrors = validateField(zipCodeInput, zipCodeType, ariaDescribedBy, {
		errors: previousInputGroupErrors,
		labelFor: 'billingZip',
		errorMessage
	});
	return inputGroupErrors;
}

/**
 * Determine if a ZIP code note should be shown to the user
 * @param {object} zipCodeInput Input with value to check
 * @param {object} customer Customer info
 * @param {string} previousZipCodeNote Currently shown ZIP code note
 * @param {string} previousLastZipCode Last checked ZIP code
 * @param {string} previousZipCodeState Last known ZIP code state
 * @param {string} previousZipCodeCity Last known ZIP code city
 * @param {boolean} shouldCheckZip Condition to be met for API call
 * @param {string} zipMessage Message to be shown in ZIP code note
 * @returns {object}
 */
export async function validateZipAsync(zipCodeInput, customer, previousZipCodeNote, previousLastZipCode, previousZipCodeState, previousZipCodeCity, shouldCheckZip, zipMessage) { // eslint-disable-line func-style
	const zipCodeValue = zipCodeInput.value.split('-')[0];
	let zipCodeNote = previousZipCodeNote;
	let lastZipCode = previousLastZipCode;
	let zipCodeState = previousZipCodeState;
	let zipCodeCity = previousZipCodeCity;
	if (!env.zipMapCountries.includes(customer.billing.country)) {
		zipCodeNote = '';
		lastZipCode = '';
		zipCodeState = '';
		zipCodeCity = '';
	} else if (previousLastZipCode !== zipCodeValue) {
		if (shouldCheckZip && zipCodeValue.length > 2) {
			// @note We use .apply here so we can pass correct context of Vue component to the isStateZipCode method
			const matchedCityAndState = await isStateZipCode.apply(this, [zipCodeValue, customer.billing.country]);
			zipCodeNote = !isObject(matchedCityAndState) ? zipMessage : '';
			lastZipCode = zipCodeValue;
			zipCodeState = isObject(matchedCityAndState) ? matchedCityAndState.state : '';
			zipCodeCity = isObject(matchedCityAndState) ? matchedCityAndState.city : '';
		} else {
			zipCodeNote = '';
			zipCodeState = '';
			zipCodeCity = '';
		}
	}
	return {
		zipCodeNote,
		lastZipCode,
		zipCodeState,
		zipCodeCity
	};
}
